import React, { useCallback, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import {
  Box,
  CircularProgress,
  IconButton,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import { ImageZoomPan, CommonButton } from 'gemlux-common-components';
import { theme as AppTheme } from '../../../theme/theme';
import { useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import PlaceholderGemSvg from '../../../assets/img/gem-svgrepo-com.svg';
import useEnv from '../../../utils/use-env-hook';
import { IGemMedia } from '../../../redux/gem/gemSlice';
import imageIcon from '../../../assets/img/gem-image.svg';
import videoPlayIcon from '../../../assets/img/video-play-icon-new.svg';
import imageIconW from '../../../assets/img/gem-image-white.svg';
import videoPlayIconW from '../../../assets/img/video-play-icon-white.svg';
import { GemMediaScanType } from '../../../utils/enums/gem-media-scan-type.enum';
import { GemMediaType } from '../../../utils/enums/gem-media-type.enum';
import PlayIcon from '../../../assets/img/solar_play-bold.svg';
import VideoPlayer from './VideoPlayer';
import AlertFill from '../../../assets/img/alert-fill.svg';
import { AiStatus } from '../../../utils/enums/gem-ai-status.enum';

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: AppTheme.palette.primary.dark,
    color: 'white',
    boxShadow: theme.shadows[1],
    fontSize: 13,
    fontWeight: 700,
    padding: '10px',
    borderRadius: '4px',
  },
}));

const AiStatusIndicator: React.FC<{ status: AiStatus; attempts: number }> = ({ status, attempts }) => {
  if (status === AiStatus.PENDING || (status === AiStatus.IN_PROGRESS && attempts === 1)) {
    return (
      <Box display="flex" alignItems="center" mt={1} gap={1} marginBottom="5px">
        <CircularProgress size={16} sx={{ color: AppTheme.palette.primary.main }} />
        <Typography variant="textBase500" color="#14181F">
          Masking Under Process..
        </Typography>
      </Box>
    );
  }

  if (status === AiStatus.IN_PROGRESS && attempts > 1) {
    return (
      <Box display="flex" alignItems="center" mt={1} gap={1} marginBottom="5px">
        <CircularProgress size={16} sx={{ color: AppTheme.palette.primary.main }} />
        <Typography variant="textBase500" color="#14181F">
          Retrying..
        </Typography>
      </Box>
    );
  }

  if (status === AiStatus.FAILED) {
    return (
      <Box display="flex" alignItems="center" mt={1} gap={1} marginBottom="5px">
        <Box component="img" src={AlertFill} alt="Alert Icon" />
        <Typography variant="textBase500" color="#DF0C3D">
          Masking Failed!
        </Typography>
      </Box>
    );
  }

  return null;
};

function GemGallery() {
  const [scanType, setScanType] = useState<GemMediaScanType | null>(null);
  const [mediaType, setMediaType] = useState<GemMediaType | null>(null);
  const [filteredMedia, setFilteredMedia] = useState<IGemMedia[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  const [openZoomPan, setOpenZoomPan] = useState(false);
  const [zoomPanTitle, setZoomPanTitle] = useState('');
  const [zoomPanImage, setZoomPanImage] = useState<React.ReactNode | null>(null);

  const gem = useAppSelector((state: RootState) => state.gem.gem);
  const { baseUrl = '' } = useEnv();

  const handleFilterMedia = useCallback(() => {
    if (!gem) return;

    let mediaToFilter =
      gem.media?.filter((media) =>
        [GemMediaScanType.Appearance, GemMediaScanType.CatGrowth, GemMediaScanType.CatTreatment].includes(
          media.scanType,
        ),
      ) || [];
    if (scanType) {
      mediaToFilter = mediaToFilter.filter((media) =>
        scanType === GemMediaScanType.Inclusions
          ? [GemMediaScanType.CatGrowth, GemMediaScanType.CatTreatment].includes(media.scanType)
          : media.scanType === scanType,
      );
    }
    if (mediaType) {
      mediaToFilter = mediaToFilter.filter((media) =>
        mediaType === GemMediaType.Image
          ? media.mediaType === mediaType
          : media.view === 'Thumbnail' || media.mediaType === mediaType,
      );
    }
    setFilteredMedia(mediaToFilter);
  }, [gem, scanType, mediaType]);

  useEffect(() => {
    handleFilterMedia();
  }, [handleFilterMedia]);

  const handleMediaTypeChange = (type: GemMediaType) => {
    setMediaType((prev) => (prev === type ? null : type));
  };

  const getImageIcon = (mediaT: GemMediaType) => {
    if (mediaT === GemMediaType.Image) {
      return mediaType === mediaT ? imageIconW : imageIcon;
    }
    return mediaType === mediaT ? videoPlayIconW : videoPlayIcon;
  };

  const getVideoThumbnail = (video: IGemMedia) =>
    filteredMedia.find(
      (m) => m.mediaType === GemMediaType.Image && m.view === 'Thumbnail' && m.scanType === video.scanType,
    );

  const getMediaSource = (media: IGemMedia) =>
    `${baseUrl}/${media.mediaType === GemMediaType.Video ? media.location.replace(/\.mp4$/, '.png') : media.location}`;

  const handleVideoClick = (gemScanType: GemMediaScanType) => {
    const selectedVideoLocation = filteredMedia.find(
      (m) => m.mediaType === GemMediaType.Video && m.scanType === gemScanType && m.view,
    )?.location;
    if (selectedVideoLocation) {
      setSelectedVideo(`${baseUrl}/${selectedVideoLocation}`);
    }
  };

  const handleImageClick = (media: IGemMedia) => {
    setZoomPanTitle(media.scanType || 'Image');
    setZoomPanImage(
      <img
        src={getMediaSource(media)}
        alt={media.scanType || 'Selected Image'}
        style={{ width: '100%', height: 'auto' }}
      />,
    );
    setOpenZoomPan(true);
  };

  const renderMediaCard = (media: IGemMedia, showPlayIcon: boolean, thumbnailMedia?: IGemMedia) => {
    const sourceUrl = thumbnailMedia ? `${baseUrl}/${thumbnailMedia.location}` : getMediaSource(media);

    return (
      <CustomTooltip title={showPlayIcon ? media.scanType : media.view} followCursor key={media.media_id}>
        <Box
          sx={{
            position: 'relative',
            borderRadius: '5px',
            cursor: showPlayIcon ? 'pointer' : 'zoom-in',
          }}
          onClick={showPlayIcon ? () => handleVideoClick(media.scanType) : () => handleImageClick(media)}
        >
          <Box
            data-testid="gem_media"
            component="img"
            src={sourceUrl}
            borderRadius="5px"
            onError={(event) => {
              const img = event.currentTarget as HTMLImageElement;
              img.src = PlaceholderGemSvg;
            }}
            sx={{
              height: '210px',
              objectFit: 'contain',
              width: '100%',
            }}
          />
          {showPlayIcon && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                component="img"
                src={PlayIcon}
                sx={{
                  width: '48px',
                  height: '48px',
                }}
              />
            </Box>
          )}
        </Box>
      </CustomTooltip>
    );
  };
  return gem ? (
    <Grid
      p={3}
      display="flex"
      flexDirection="column"
      borderRadius="0 0 4px 4px"
      border={`1px solid ${AppTheme.palette.shades.grayBorder}`}
      gap={3}
      borderTop="none"
    >
      <Grid container>
        {/* Scan Type Buttons */}
        <Grid width="100%" display="flex" alignItems="center">
          <Grid
            container
            rowGap={3}
            padding={2}
            sx={{
              flexWrap: 'nowrap',
              alignItems: 'center',
            }}
          >
            {[null, GemMediaScanType.Appearance, GemMediaScanType.Inclusions].map((type) => (
              <CommonButton
                key={type || 'all'}
                padding="12px 24px"
                radius="8px"
                onClick={() => setScanType(type)}
                backgroundColor={scanType === type ? '#3348FF14' : 'transparent'}
                textColor={scanType === type ? '#3348FF' : 'black'}
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '15px',
                  fontWeight: 500,
                  textTransform: 'none',
                }}
              >
                {type || 'All'}
              </CommonButton>
            ))}
            {/* AI Status Indicators */}
            <Box display="flex" flexDirection="column" ml="auto" gap={1}>
              <AiStatusIndicator status={gem.aiStatus} attempts={gem.aiTryAttempts ?? 0} />
            </Box>
          </Grid>
          {/* Media Type Buttons */}
          <Grid>
            <Grid display="flex" flexDirection="row" gap={2}>
              {[GemMediaType.Image, GemMediaType.Video].map((type) => (
                <IconButton
                  key={type}
                  role="button"
                  size="small"
                  onClick={() => handleMediaTypeChange(type)}
                  data-testid={type === GemMediaType.Image ? 'img_button' : 'video_button'}
                  sx={{
                    backgroundColor: mediaType === type ? '#3348FF14' : 'transparent',
                    borderRadius: '8px',
                    height: '40px',
                    width: '40px',
                    marginRight: '2px',
                    border: `1px solid ${mediaType === type ? '#3348FF' : AppTheme.palette.shades.grayBorder}`,
                    padding: '8px 12px',
                  }}
                >
                  <Box component="img" src={getImageIcon(type)} />
                </IconButton>
              ))}
            </Grid>
          </Grid>
        </Grid>
        {/* Media Cards */}
        <Grid container gap={3}>
          {/* Render Videos */}
          {filteredMedia
            ?.filter((m) => m.mediaType === GemMediaType.Video)
            .map((video) => {
              const thumbnail = getVideoThumbnail(video);
              return renderMediaCard(video, true, thumbnail);
            })}

          {/* Render Images */}
          {filteredMedia
            ?.filter((media) => media.mediaType === GemMediaType.Image && media.view !== 'Thumbnail')
            .map((media) => renderMediaCard(media, false))}
        </Grid>

        {/* Video Player */}
        {selectedVideo && (
          <VideoPlayer
            data-testid="video-player"
            open={!!selectedVideo}
            onClose={() => setSelectedVideo(null)}
            videoUrl={selectedVideo}
          />
        )}
      </Grid>

      {openZoomPan && (
        <ImageZoomPan
          open={openZoomPan}
          title={zoomPanTitle}
          onClose={() => setOpenZoomPan(false)}
          image={zoomPanImage}
        />
      )}
    </Grid>
  ) : null;
}

export default GemGallery;
